import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, Save, Printer } from "lucide-react";
import WhatsAppShareButton from "../../components/WhatsAppShareButton";
import ItemSearchPicker from "../../components/ItemSearchPicker";
import CustomerMasterPicker from "../../components/CustomerMasterPicker";
import { toast, Toaster } from "sonner";

const blankCust = { name: "", customer_type: "retailer", phone: "", email: "", gst_number: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "" };

export default function AccountsInvoiceEditor() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const orderId = params.get("order_id");
    const nav = useNavigate();

    const [customers, setCustomers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [order, setOrder] = useState(null);
    const [customer, setCustomer] = useState(blankCust);
    const [customer_master_id, setCmid] = useState("");
    const [items, setItems] = useState([{ name: "", size_label: "", hsn: "", quantity: 1, rate: 0, amount: 0 }]);
    const [gst_pct, setGst] = useState(12);
    const [splitGst, setSplitGst] = useState(true);
    const [discount_pct, setDisc] = useState(0);
    const [special_discount, setSpec] = useState(0);
    const [cash_discount, setCash] = useState(0);
    const [round_off, setRound] = useState(0);
    const [template_id, setTemplate] = useState("");
    const [notes, setNotes] = useState("");
    const [terms, setTerms] = useState("");
    const [busy, setBusy] = useState(false);
    const [loadedInv, setLoadedInv] = useState(null);
    // Distributor auto-fill — populated by /distributor-hub/_match when a distributor is picked
    const [billingNotes, setBillingNotes] = useState("");
    const [bonusNotes, setBonusNotes] = useState("");

    useEffect(() => {
        api.get("/customers").then(r => setCustomers(r.data));
        api.get("/invoice-templates").then(r => {
            setTemplates(r.data);
            const def = r.data.find(t => t.is_default) || r.data[0];
            if (def) { setTemplate(def.id); setTerms(def.terms || ""); }
        });
        if (id) {
            api.get(`/invoices/${id}`).then(r => {
                const i = r.data;
                setLoadedInv(i);
                setCustomer(i.customer || blankCust);
                setCmid(i.customer_master_id || "");
                setItems(i.items.length ? i.items : items);
                setGst(i.gst_pct); setDisc(i.discount_pct);
                setSpec(i.special_discount); setCash(i.cash_discount);
                setRound(i.round_off); setNotes(i.notes || ""); setTerms(i.terms || "");
                setTemplate(i.template_id || "");
                setSplitGst(i.cgst_pct != null);
            });
        } else if (orderId) {
            api.get(`/orders/${orderId}`).then(r => {
                const o = r.data; setOrder(o);
                // Prefill customer + items from order's packed_items
                const obo = o.on_behalf_of || null;
                if (obo) {
                    setCustomer({
                        name: obo.name || o.address?.name || "",
                        customer_type: obo.customer_type || "retailer",
                        phone: obo.phone || o.address?.phone || "",
                        email: "", gst_number: "",
                        address_line1: o.address?.line1 || "", address_line2: o.address?.line2 || "",
                        city: o.address?.city || "", state: o.address?.state || "", pincode: o.address?.pincode || "",
                    });
                    setCmid(obo.customer_master_id || "");
                } else {
                    setCustomer({ ...blankCust, name: o.address?.name || "", phone: o.address?.phone || "",
                        address_line1: o.address?.line1 || "", address_line2: o.address?.line2 || "",
                        city: o.address?.city || "", state: o.address?.state || "", pincode: o.address?.pincode || "",
                    });
                }
                const newItems = o.items.map((it, idx) => {
                    const packed = o.packing?.items?.[idx]?.packed_quantity ?? it.quantity;
                    const avail = o.packing?.items?.[idx]?.available !== false;
                    if (!avail) return null;
                    return { name: it.product_name, size_label: it.size_label, hsn: "", quantity: packed, rate: it.price, amount: packed * it.price };
                }).filter(Boolean);
                if (newItems.length) setItems(newItems);
            });
        }
        // eslint-disable-next-line
    }, []);

    /** Picker-aware variant: receives the full customer doc (or null when cleared). */
    const onPickFromMaster = async (c) => {
        if (!c) { setCmid(""); setBillingNotes(""); setBonusNotes(""); return; }
        setCmid(c.id);
        setCustomer({
            name: c.name || "",
            customer_type: c.customer_type || "retailer",
            phone: c.phone || "",
            email: c.email || "",
            gst_number: c.gst_number || "",
            address_line1: c.address_line1 || "",
            address_line2: c.address_line2 || "",
            city: c.city || "",
            state: c.state || "",
            pincode: c.pincode || "",
        });
        setGst(c.default_gst_pct || 12);
        setDisc(c.default_discount_pct || 0);
        if (!customers.find(x => x.id === c.id)) setCustomers(cur => [...cur, c]);

        // If this customer master is linked to a distributor user, auto-apply the
        // active discount tier + cash discount + bonus / billing notes.
        if (c.customer_type === "distributor" && c.user_id) {
            try {
                const currentSubtotal = items.reduce((s, it) => s + ((it.quantity || 0) * (it.rate || 0)), 0);
                const { data: m } = await api.get("/distributor-hub/_match", {
                    params: { user_id: c.user_id, subtotal: currentSubtotal },
                });
                if (typeof m.discount_pct === "number") setDisc(m.discount_pct);
                if (typeof m.cash_discount_pct === "number") setCash(m.cash_discount_pct);
                setBillingNotes(m.billing_notes || "");
                setBonusNotes(m.bonus_notes || "");
                if (m.tier_label) toast.success(`Applied "${m.tier_label}" tier · ${m.discount_pct}% discount${m.cash_discount_pct ? ` + ${m.cash_discount_pct}% cash` : ""}`);
            } catch (e) {
                // The tier-match endpoint is optional — fall back to defaults.
                console.debug("Distributor tier match unavailable:", e.response?.status);
            }
        } else {
            setBillingNotes("");
            setBonusNotes("");
        }
    };

    /** Save the currently-typed customer fields as a NEW master record, then auto-select it. */
    const saveCurrentAsNewMaster = async () => {
        if (!customer.name || !customer.name.trim()) {
            toast.error("Type a customer name first, then click 'Save as new'");
            return;
        }
        try {
            const { data } = await api.post("/customers", {
                name: customer.name,
                customer_type: customer.customer_type || "retailer",
                phone: customer.phone || "",
                email: customer.email || "",
                gst_number: customer.gst_number || "",
                address_line1: customer.address_line1 || "",
                address_line2: customer.address_line2 || "",
                city: customer.city || "",
                state: customer.state || "",
                pincode: customer.pincode || "",
                default_gst_pct: gst_pct || 12,
                default_discount_pct: discount_pct || 0,
            });
            setCustomers(cur => [...cur, data]);
            setCmid(data.id);
            toast.success(`Saved "${data.name}" to customer master`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save to master");
        }
    };

    const selectedMaster = customer_master_id ? (customers.find(c => c.id === customer_master_id) || null) : null;

    const updateItem = (i, patch) => setItems(cur => cur.map((it, idx) => idx === i ? { ...it, ...patch, amount: ((patch.quantity ?? it.quantity) || 0) * ((patch.rate ?? it.rate) || 0) } : it));
    const addItem = () => setItems(cur => [...cur, { name: "", size_label: "", hsn: "", quantity: 1, rate: 0, amount: 0 }]);
    const removeItem = (i) => setItems(cur => cur.filter((_, idx) => idx !== i));

    /** Append an item from the unified search picker. Skips duplicates by (name+size) — bumps quantity instead. */
    const addItemFromSearch = (picked) => {
        setItems((cur) => {
            const existingIdx = cur.findIndex((it) =>
                it.name.toLowerCase() === picked.name.toLowerCase()
                && it.size_label.toLowerCase() === picked.size_label.toLowerCase(),
            );
            if (existingIdx >= 0) {
                const next = [...cur];
                const qty = (next[existingIdx].quantity || 0) + 1;
                next[existingIdx] = { ...next[existingIdx], quantity: qty, amount: qty * (next[existingIdx].rate || picked.rate || 0) };
                toast.message(`+1 of ${picked.name} ${picked.size_label}`);
                return next;
            }
            return [...cur, {
                name: picked.name,
                size_label: picked.size_label,
                hsn: picked.hsn || "",
                quantity: 1,
                rate: picked.rate || 0,
                amount: picked.rate || 0,
                sku: picked.sku || "",
            }];
        });
    };

    const calc = useMemo(() => {
        const subtotal = items.reduce((s, it) => s + (it.quantity || 0) * (it.rate || 0), 0);
        const discount_amount = +(subtotal * discount_pct / 100).toFixed(2);
        const after_discount = subtotal - discount_amount;
        const gst_amount = +(after_discount * gst_pct / 100).toFixed(2);
        const pre = after_discount + gst_amount - special_discount - cash_discount;
        const auto_round = +(Math.round(pre) - pre).toFixed(2);
        const ro = (Math.abs(round_off) > 0) ? round_off : auto_round;
        const total = +(after_discount + gst_amount - special_discount - cash_discount + ro).toFixed(2);
        return { subtotal, discount_amount, after_discount, gst_amount, round_off: ro, total };
    }, [items, gst_pct, discount_pct, special_discount, cash_discount, round_off]);

    const save = async () => {
        if (!customer.name) return toast.error("Customer name is required");
        if (items.length === 0 || items.some(i => !i.name || !i.quantity || !i.rate)) return toast.error("Fill all items");
        setBusy(true);
        const body = {
            order_id: orderId || (order?.id ?? null),
            template_id,
            customer,
            customer_master_id: customer_master_id || null,
            items: items.map(i => ({ ...i, quantity: parseInt(i.quantity), rate: parseFloat(i.rate), amount: parseFloat(i.quantity) * parseFloat(i.rate) })),
            gst_pct,
            cgst_pct: splitGst ? gst_pct / 2 : null,
            sgst_pct: splitGst ? gst_pct / 2 : null,
            igst_pct: splitGst ? null : gst_pct,
            discount_pct,
            special_discount,
            cash_discount,
            round_off: Math.abs(round_off) > 0 ? round_off : 0,
            notes, terms,
        };
        try {
            const { data } = id ? await api.put(`/invoices/${id}`, body) : await api.post("/invoices", body);
            toast.success(`Invoice ${data.invoice_number} saved`);
            nav(`/employee/accounts/invoices/${data.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="invoice-editor" className="space-y-4">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-eyebrow text-sky-700">{id ? "Edit" : "New"} invoice</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Tally-style billing</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {id && loadedInv && <WhatsAppShareButton invoice={loadedInv} variant="outline" label="WhatsApp" testId="inv-editor-wa" />}
                    {id && <Link to={`/invoice/${id}`} target="_blank"><Button variant="outline" className="rounded-none border-stone-400"><Printer className="w-4 h-4 mr-2" />Preview / Print</Button></Link>}
                    <Button onClick={save} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="invoice-save"><Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save invoice"}</Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <section className="lg:col-span-2 space-y-4">
                    <div className="bg-white border border-stone-200 p-4 space-y-3">
                        <CustomerMasterPicker
                            value={selectedMaster}
                            onPick={onPickFromMaster}
                            onSaveAsNew={saveCurrentAsNewMaster}
                            showTypeSelector
                            typeValue={customer.customer_type}
                            onTypeChange={(v) => setCustomer(c => ({ ...c, customer_type: v }))}
                            dataTestIdPrefix="inv-cmp"
                        />
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Template</Label>
                                <Select value={template_id} onValueChange={setTemplate}>
                                    <SelectTrigger className="rounded-none border-stone-300 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.style}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <CF label="Customer name *" v={customer.name} on={(v) => setCustomer(c => ({ ...c, name: v }))} t="cust-name" />
                            <CF label="Phone" v={customer.phone} on={(v) => setCustomer(c => ({ ...c, phone: v }))} t="cust-phone" />
                            <CF label="GSTIN" v={customer.gst_number} on={(v) => setCustomer(c => ({ ...c, gst_number: v }))} t="cust-gstin" />
                            <CF label="Email" v={customer.email} on={(v) => setCustomer(c => ({ ...c, email: v }))} />
                            <CF label="Address line 1" v={customer.address_line1} on={(v) => setCustomer(c => ({ ...c, address_line1: v }))} />
                            <CF label="Address line 2" v={customer.address_line2} on={(v) => setCustomer(c => ({ ...c, address_line2: v }))} />
                            <CF label="City" v={customer.city} on={(v) => setCustomer(c => ({ ...c, city: v }))} />
                            <CF label="State" v={customer.state} on={(v) => setCustomer(c => ({ ...c, state: v }))} />
                        </div>
                    </div>

                    <div className="bg-white border border-stone-200">
                        <div className="p-3 border-b border-stone-200 flex items-center justify-between gap-3 flex-wrap">
                            <div className="font-serif text-xl shrink-0">Line items</div>
                            <div className="flex-1 min-w-[260px] max-w-2xl">
                                <ItemSearchPicker
                                    onPick={addItemFromSearch}
                                    placeholder="Search medicine, size or SKU and press Enter to add…"
                                    testId="inv-item-search"
                                />
                            </div>
                            <Button onClick={addItem} variant="outline" className="rounded-none border-stone-400" data-testid="inv-add-item"><Plus className="w-4 h-4 mr-1" />Blank line</Button>
                        </div>
                        <table className="w-full text-sm">
                            <thead><tr className="bg-stone-50 border-b border-stone-200">
                                <th className="text-left p-2 text-xs uppercase tracking-widest text-stone-500 w-1/3">Description</th>
                                <th className="text-left p-2 text-xs uppercase tracking-widest text-stone-500">Size</th>
                                <th className="text-left p-2 text-xs uppercase tracking-widest text-stone-500">HSN</th>
                                <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Qty</th>
                                <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Rate</th>
                                <th className="text-right p-2 text-xs uppercase tracking-widest text-stone-500">Amount</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={i} className="border-b border-stone-100">
                                        <td className="p-1.5"><Input value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} className="rounded-none border-stone-200 h-8" data-testid={`inv-item-name-${i}`} /></td>
                                        <td className="p-1.5"><Input value={it.size_label} onChange={(e) => updateItem(i, { size_label: e.target.value })} className="rounded-none border-stone-200 h-8 w-24" /></td>
                                        <td className="p-1.5"><Input value={it.hsn} onChange={(e) => updateItem(i, { hsn: e.target.value })} className="rounded-none border-stone-200 h-8 w-24" /></td>
                                        <td className="p-1.5"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: parseFloat(e.target.value || 0) })} className="rounded-none border-stone-200 h-8 w-20 text-right" data-testid={`inv-item-qty-${i}`} /></td>
                                        <td className="p-1.5"><Input type="number" step="0.01" value={it.rate} onChange={(e) => updateItem(i, { rate: parseFloat(e.target.value || 0) })} className="rounded-none border-stone-200 h-8 w-24 text-right" data-testid={`inv-item-rate-${i}`} /></td>
                                        <td className="p-1.5 text-right font-medium pr-3">{formatINR((it.quantity || 0) * (it.rate || 0))}</td>
                                        <td className="p-1.5 pr-2"><button onClick={() => removeItem(i)} className="text-red-700"><Trash2 className="w-4 h-4" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white border border-stone-200 p-4 space-y-3">
                        <div className="font-serif text-xl">Notes & Terms</div>
                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes…" className="rounded-none border-stone-300" />
                        <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms (printed on invoice)…" className="rounded-none border-stone-300" />
                    </div>

                    {(billingNotes || bonusNotes) && (
                        <div className="bg-amber-50 border border-amber-200 p-4 space-y-3" data-testid="inv-dist-notes">
                            <div className="font-serif text-lg text-amber-900">Distributor reminders</div>
                            {billingNotes && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-1">Billing notes</div>
                                    <pre className="text-xs text-stone-800 whitespace-pre-wrap font-sans">{billingNotes}</pre>
                                </div>
                            )}
                            {bonusNotes && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-1">Bonus structure (apply manually if applicable)</div>
                                    <pre className="text-xs text-stone-800 whitespace-pre-wrap font-sans">{bonusNotes}</pre>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <aside className="bg-white border border-stone-200 p-4 space-y-3 self-start">
                    <div className="font-serif text-xl">Totals</div>
                    <div className="text-sm flex justify-between"><span>Subtotal</span><span>{formatINR(calc.subtotal)}</span></div>
                    <NumRow label="Discount %" v={discount_pct} on={setDisc} suffix="%" testId="inv-disc" />
                    <div className="text-sm flex justify-between text-stone-500"><span>Discount amount</span><span>− {formatINR(calc.discount_amount)}</span></div>
                    <div className="text-sm flex justify-between"><span>After discount</span><span>{formatINR(calc.after_discount)}</span></div>
                    <NumRow label="GST %" v={gst_pct} on={setGst} suffix="%" testId="inv-gst" />
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={splitGst} onChange={(e) => setSplitGst(e.target.checked)} /> Intra-state (split CGST/SGST)</label>
                    <div className="text-sm flex justify-between text-stone-500"><span>GST amount</span><span>+ {formatINR(calc.gst_amount)}</span></div>
                    <NumRow label="Special discount (₹)" v={special_discount} on={setSpec} testId="inv-special" />
                    <NumRow label="Cash discount (₹)" v={cash_discount} on={setCash} testId="inv-cash" />
                    <NumRow label="Round off (₹)" v={round_off} on={setRound} hint={`auto: ${calc.round_off}`} testId="inv-round" />
                    <div className="text-base flex justify-between border-t border-stone-200 pt-3 font-serif text-2xl text-stone-900"><span>Total</span><span>{formatINR(calc.total)}</span></div>
                </aside>
            </div>
        </div>
    );
}

const CF = ({ label, v, on, t }) => (
    <div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input value={v || ""} onChange={(e) => on(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid={t} /></div>
);
const NumRow = ({ label, v, on, suffix, hint, testId }) => (
    <div>
        <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-stone-600">{label}</Label>
            <Input type="number" step="0.01" value={v} onChange={(e) => on(parseFloat(e.target.value || 0))} className="rounded-none border-stone-300 h-8 w-32 text-right" data-testid={testId} />
        </div>
        {hint && <div className="text-[10px] text-stone-400 text-right">{hint}{suffix ? ` ${suffix}` : ""}</div>}
    </div>
);
